import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDropzone } from 'react-dropzone';
import { Upload, X, ArrowLeft, FileText, Info } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';
import VideoUploader from '@/components/VideoUploader';

// Incoterms 2020
const INCOTERMS = [
  { code: "EXW", name: "Ex Works", description: "Seller delivers goods at their premises" },
  { code: "FCA", name: "Free Carrier", description: "Seller delivers goods to carrier nominated by buyer" },
  { code: "CPT", name: "Carriage Paid To", description: "Seller pays freight to destination" },
  { code: "CIP", name: "Carriage and Insurance Paid To", description: "Seller pays freight and insurance" },
  { code: "DAP", name: "Delivered at Place", description: "Seller delivers goods ready for unloading" },
  { code: "DPU", name: "Delivered at Place Unloaded", description: "Seller delivers goods unloaded" },
  { code: "DDP", name: "Delivered Duty Paid", description: "Seller bears all costs including duties" },
  { code: "FAS", name: "Free Alongside Ship", description: "Seller delivers goods alongside vessel" },
  { code: "FOB", name: "Free on Board", description: "Seller delivers goods on board vessel" },
  { code: "CFR", name: "Cost and Freight", description: "Seller pays costs and freight" },
  { code: "CIF", name: "Cost, Insurance and Freight", description: "Seller pays costs, insurance and freight" },
];

export default function CreateListing() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'coltan',
    description: '',
    price: '',
    quantity: '',
    unit: 'tons',
    location: '',
    grade: '',
    images: [],
    videos: [],
    certifications: [],
    specifications: {},
    incoterm: 'EXW',
    terms_of_business: '',
    payment_terms: '',
    loading_port: '',
    discharge_port: ''
  });
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    if (acceptedFiles.length > 5) {
      toast.error('Maximum 5 images per listing');
      return;
    }

    const uploadPromises = acceptedFiles.map(async (file) => {
      // Hard size limit so we fail fast before hitting Vercel's 4.5 MB body limit
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 8 MB)`);
        return null;
      }
      const formDataImg = new FormData();
      formDataImg.append('file', file);
      try {
        const response = await axios.post(`${API_URL}/upload-image`, formDataImg, {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000,
        });
        return response.data.url;
      } catch (error) {
        const msg = error.response?.data?.detail || error.message || 'Upload failed';
        toast.error(`${file.name}: ${msg}`);
        return null;
      }
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    const validUrls = uploadedUrls.filter(url => url !== null);
    if (validUrls.length > 0) {
      toast.success(`${validUrls.length} image(s) uploaded`);
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, ...validUrls] }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: true
  });

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/minerals`,
        {
          ...formData,
          price: parseFloat(formData.price),
          quantity: parseFloat(formData.quantity)
        },
        { headers: getAuthHeader() }
      );
      toast.success(t('create.success'));
      navigate('/seller');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('create.fail'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/seller')}
          className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </button>

        <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-8">
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="create-listing-title">
            {t('create.title')}
          </h1>
          <p className="text-[#94A3B8] mb-8">{t('create.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-white font-medium">{t('create.field.title')} *</Label>
              <Input
                id="title"
                data-testid="title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Premium Hematite Iron Ore"
                className="mt-1 border-[#1E293B]"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="category" className="text-white font-medium">{t('create.field.category')} *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coltan">Coltan (Tantalite)</SelectItem>
                    <SelectItem value="copper">Copper</SelectItem>
                    <SelectItem value="cobalt">Cobalt</SelectItem>
                    <SelectItem value="lithium">Lithium</SelectItem>
                    <SelectItem value="manganese">Manganese</SelectItem>
                    <SelectItem value="chromium">Chromium</SelectItem>
                    <SelectItem value="iron_ore">Iron Ore</SelectItem>
                    <SelectItem value="gemstones">Gemstones</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="diamonds">Diamonds</SelectItem>
                    <SelectItem value="rare_earth">Rare Earth Elements</SelectItem>
                    <SelectItem value="bauxite">Bauxite/Aluminum</SelectItem>
                    <SelectItem value="other">Other Minerals</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="grade" className="text-white font-medium">{t('create.field.grade')} *</Label>
                <Input
                  id="grade"
                  data-testid="grade-input"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  required
                  placeholder={
                    formData.category === 'coltan' ? 'e.g., 25% Ta2O5 (min 20%)' :
                    formData.category === 'copper' ? 'e.g., 28% Cu' :
                    formData.category === 'cobalt' ? 'e.g., 10% Co' :
                    formData.category === 'lithium' ? 'e.g., 6% Li2O' :
                    formData.category === 'gold' ? 'e.g., 95% purity' :
                    'e.g., Grade A, 65% Fe'
                  }
                  className="mt-1 border-[#1E293B]"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  {formData.category === 'coltan' ? 'Coltan: Minimum 20% Ta2O5 content' :
                   formData.category === 'copper' ? 'Copper: Industry standard 25-35% Cu' :
                   formData.category === 'cobalt' ? 'Cobalt: Typical 8-15% Co' :
                   'Specify internationally recognized grade/purity'}
                </p>
              </div>

              <div>
                <Label htmlFor="location" className="text-white font-medium">{t('create.field.location')} *</Label>
                <Input
                  id="location"
                  data-testid="location-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  placeholder="e.g., Dar es Salaam, Tanzania"
                  className="mt-1 border-[#1E293B]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-white font-medium">{t('create.field.description')} *</Label>
              <Textarea
                id="description"
                data-testid="description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={5}
                placeholder="Describe your mineral in detail..."
                className="mt-1 border-[#1E293B]"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="price" className="text-white font-medium">{t('create.field.price')} *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  data-testid="price-input"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  placeholder="100.00"
                  className="mt-1 border-[#1E293B]"
                />
              </div>

              <div>
                <Label htmlFor="quantity" className="text-white font-medium">{t('create.field.quantity')} *</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  data-testid="quantity-input"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  placeholder="1000"
                  className="mt-1 border-[#1E293B]"
                />
              </div>

              <div>
                <Label htmlFor="unit" className="text-white font-medium">{t('create.field.unit')} *</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tons">Tons</SelectItem>
                    <SelectItem value="kg">Kilograms</SelectItem>
                    <SelectItem value="carats">Carats</SelectItem>
                    <SelectItem value="ounces">Ounces</SelectItem>
                    <SelectItem value="pieces">Pieces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Incoterm and Terms of Business Section */}
            <div className="border-t border-[#1E293B] pt-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F59E0B]" />
                Trade Terms
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="incoterm" className="text-white font-medium">
                    Incoterm (Delivery Terms) *
                  </Label>
                  <Select
                    value={formData.incoterm}
                    onValueChange={(value) => setFormData({ ...formData, incoterm: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="incoterm-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INCOTERMS.map((term) => (
                        <SelectItem key={term.code} value={term.code}>
                          {term.code} - {term.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-xs text-[#3B82F6] flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>{INCOTERMS.find(t => t.code === formData.incoterm)?.name}:</strong>{' '}
                        {INCOTERMS.find(t => t.code === formData.incoterm)?.description}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="loading_port" className="text-white font-medium">
                      Loading Port / Origin (Optional)
                    </Label>
                    <Input
                      id="loading_port"
                      data-testid="loading-port-input"
                      value={formData.loading_port}
                      onChange={(e) => setFormData({ ...formData, loading_port: e.target.value })}
                      placeholder="e.g. Mwanza, Tanzania"
                      className="mt-1 border-[#1E293B]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="discharge_port" className="text-white font-medium">
                      Buyer&apos;s Discharge Port (Optional)
                    </Label>
                    <Input
                      id="discharge_port"
                      data-testid="discharge-port-input"
                      value={formData.discharge_port}
                      onChange={(e) => setFormData({ ...formData, discharge_port: e.target.value })}
                      placeholder="e.g. Shanghai, China"
                      className="mt-1 border-[#1E293B]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="payment_terms" className="text-white font-medium">
                    Payment Terms
                  </Label>
                  <Select
                    value={formData.payment_terms || 'default'}
                    onValueChange={(v) =>
                      setFormData({ ...formData, payment_terms: v === 'default' ? '' : v })
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="payment-terms-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">
                        Platform Standard (30% TT advance · 70% TT vs B/L)
                      </SelectItem>
                      <SelectItem value="100% TT advance payment before shipment.">
                        100% TT Advance (before shipment)
                      </SelectItem>
                      <SelectItem value="50% TT advance, 50% TT against bill of lading.">
                        50/50 TT (advance & B/L)
                      </SelectItem>
                      <SelectItem value="30% TT advance, 70% TT against bill of lading.">
                        30/70 TT (advance & B/L) — recommended
                      </SelectItem>
                      <SelectItem value="Platform Escrow — funds released on inspection pass and B/L.">
                        Platform Escrow
                      </SelectItem>
                      <SelectItem value="Irrevocable Letter of Credit (LC) at sight, confirmed by a top-50 bank.">
                        Letter of Credit (LC)
                      </SelectItem>
                      <SelectItem value="Cash Against Documents (CAD) at first-class bank.">
                        Cash Against Documents (CAD)
                      </SelectItem>
                      <SelectItem value="Cash on Delivery (COD) at agreed discharge point.">
                        Cash on Delivery (COD)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#64748B] mt-1">
                    Will be auto-filled into the Sales & Purchase Agreement
                  </p>
                </div>

                <div>
                  <Label htmlFor="terms_of_business" className="text-white font-medium">
                    Additional Terms of Business (Optional)
                  </Label>
                  <Textarea
                    id="terms_of_business"
                    data-testid="terms-of-business-input"
                    value={formData.terms_of_business}
                    onChange={(e) => setFormData({ ...formData, terms_of_business: e.target.value })}
                    rows={4}
                    placeholder="Custom clauses to append to the SPA: inspection rules, packaging, demurrage, etc."
                    className="mt-1 border-[#1E293B]"
                  />
                  <p className="text-xs text-[#64748B] mt-1">
                    Specify any additional clauses unique to this listing
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-white font-medium mb-2 block">{t('create.field.images')}</Label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-amber-500 bg-amber-50' : 'border-[#1E293B] hover:border-slate-400'
                }`}
                data-testid="image-dropzone"
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <p className="text-[#94A3B8]">
                  {isDragActive ? 'Drop images here...' : 'Drag & drop images, or click to select'}
                </p>
                <p className="text-sm text-[#64748B] mt-2">PNG, JPG up to 10MB</p>
              </div>

              {formData.images.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {formData.images.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-32 object-cover rounded-xl border border-[#1E293B]"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <VideoUploader
              value={formData.videos}
              onChange={(next) => setFormData({ ...formData, videos: next })}
            />

            <div className="flex gap-4 pt-6">
              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-listing-button"
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white py-6 text-base font-semibold"
              >
                {loading ? t('common.loading') : t('create.submit')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller')}
                data-testid="cancel-button"
                className="border-[#1E293B]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}